import React, { FC, memo, useCallback, useState, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Invoice, InvoiceCard, InvoiceStatus, calculateInvoiceStatus, generateInvoicePdfLink } from 'entities/Invoice';
import { useGetUserInvoicesQuery } from 'entities/User';
import { InvoicesModal } from './InvoicesModal';
import { ServerTable } from 'shared/ui/ServerTable';
import { ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ColumnsType } from 'antd/es/table';
import { useGetCurrencySymbol } from 'app/appState';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { ReactComponent as DownloadIcon } from 'shared/assets/icons/newIcons/DownloadIcon.svg';
import { ReactComponent as SendEmailIcon } from 'shared/assets/icons/newIcons/SendEmailIcon.svg';
import { ReactComponent as PaymentIcon } from 'shared/assets/icons/newIcons/PaymentIcon.svg';
import dayjs from 'dayjs';
import { useSendInvoiceByEmail } from 'features/SendInvoiceByEmail';
import { Modal } from 'shared/ui/Modal';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface PersonalInvoicesProps {
  userId: string | undefined;
  openPaymentModal: (invoice: Invoice) => void;
}

export const PersonalInvoices: FC<PersonalInvoicesProps> = memo((props) => {
  const { userId, openPaymentModal } = props;

  const { t } = useAppTranslation(['contracts', 'profile', 'common']);

  const [openedAccountsModal, setOpenedAccountsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Nullable<Invoice>>(null);

  const { data } = useGetUserInvoicesQuery({ userId, limit: 5 }, { skip: !userId });
  const { send } = useSendInvoiceByEmail();

  const hasRecords = (data?.content.length ?? 0) > 0;

  const today = dayjs().startOf('day');

  const openAccountsModal = useCallback(() => {
    setOpenedAccountsModal(true);
  }, []);

  const closeAccountsModal = useCallback(() => {
    setOpenedAccountsModal(false);
  }, []);

  const closeInvoiceModal = useCallback(() => {
    setSelectedInvoice(null);
  }, []);

  const openInvoiceModal = useCallback((invoice: Invoice) => {
    setSelectedInvoice(invoice);
  }, []);

  const currencySymbol = useGetCurrencySymbol();

  const columns = useMemo<ColumnsType<Invoice>>(
    () => [
      {
        title: t('Invoice number'),
        key: 'invoice',
        render: (_, record) => (
          <div className="text-primary flex flex-col text-sm">
            <span
              className="text-accent underline cursor-pointer"
              onClick={() => {
                openInvoiceModal(record);
              }}
            >
              #{record.documentNumber}
            </span>
            <span className="text-xs">{record.relatedInvoiceId && `(${t('Late charge', { ns: 'common' })})`}</span>
            <span className="text-xs">{!record.contract && `(${t('Personal Invoice', { ns: 'common' })})`}</span>
          </div>
        ),
      },
      {
        title: t('Contract'),
        key: 'contract',
        render: (_, record) => <div className="text-primary">{record.contract && `#${record.contract.contractNumber}`}</div>,
      },
      {
        title: t('Created date'),
        key: 'createdDate',
        render: (_, record) => <div className="text-primary">{getClientDateFormat(record.createdAt)}</div>,
      },
      {
        title: t('Due date'),
        key: 'dueDate',
        render: (_, record) => <div className="text-primary">{getClientDateFormat(record.dueDate)}</div>,
      },
      {
        title: t('Total amount'),
        key: 'totalAmount',
        render: (_, record) => {
          return (
            <div className="text-primary">
              {formatPrice(record.amount)} {currencySymbol}
            </div>
          );
        },
      },
      {
        title: t('Status'),
        key: 'status',
        render: (_, record) => {
          const result = calculateInvoiceStatus(record, today);

          let statusColor = 'warn';

          if (result.status === InvoiceStatus.INACTIVE && result.paidAmount > 0) statusColor = 'success';
          if (result.status === InvoiceStatus.INACTIVE && result.paidAmount === 0) statusColor = 'primaryLight';
          if (result.status === InvoiceStatus.OVERDUE) statusColor = 'error';

          let status = t('Not paid');

          if (result.status === InvoiceStatus.INACTIVE && result.paidAmount > 0) status = t('Paid');
          if (result.status === InvoiceStatus.INACTIVE && result.paidAmount === 0) status = t('Credited');
          if (result.status === InvoiceStatus.OVERDUE) status = t('Overdue');

          return <div className={`text-${statusColor}`}>{status}</div>;
        },
      },
    ],
    [t, openInvoiceModal, currencySymbol, today],
  );

  const rowActions: Array<ServerTableRowActions<Invoice>> = useMemo(
    () => [
      {
        name: 'payInvoice',
        theme: 'clear',
        icon: <PaymentIcon className="w-5 h-5" />,
        description: t('Pay', { ns: 'common' }),
        label: t('Pay', { ns: 'common' }),
        onClick: openPaymentModal,
        isDisabled: (selectedInvoice) => selectedInvoice.debt === 0,
      },
      {
        name: 'downloadInvoice',
        theme: 'clear',
        icon: <DownloadIcon className="w-5 h-5" />,
        description: t('Download', { ns: 'common' }),
        label: t('Download', { ns: 'common' }),
        onClick: (selectedInvoice) => {
          const invoiceLink = generateInvoicePdfLink(selectedInvoice.invoiceId);

          window.open(invoiceLink);
        },
      },
      {
        name: 'sendEmail',
        theme: 'clear',
        icon: <SendEmailIcon className="w-5 h-5" />,
        description: t('Send by Email'),
        label: t('Send by Email'),
        onClick: (selectedInvoice) => {
          send(selectedInvoice.invoiceId);
        },
      },
    ],
    [openPaymentModal, send, t],
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-2xl font-semibold font-tektur">{t('Personal invoices')}</div>
        {hasRecords && (
          <div className="text-accent cursor-pointer underline text-sm" onClick={openAccountsModal}>
            {t('Show all', { ns: 'profile' })}
          </div>
        )}
      </div>
      <div className="space-y-2.5 max-h-[470px] px-2 overflow-y-auto">
        {hasRecords ? (
          <ServerTable
            columns={columns}
            fetchData={useGetUserInvoicesQuery}
            rowKey="invoiceId"
            defaultFilters={{ userId }}
            rowActions={rowActions}
            pageSize={5}
            rowActionsLimit={0}
          />
        ) : (
          <div className="text-xl text-primaryLight mt-7">{t('No data', { ns: 'common' })}</div>
        )}
      </div>
      <InvoicesModal
        userId={userId}
        isOpened={openedAccountsModal}
        onCLose={closeAccountsModal}
        openInvoiceModal={openInvoiceModal}
        openPaymentModal={openPaymentModal}
      />
      <Modal isOpen={Boolean(selectedInvoice)} onClose={closeInvoiceModal} width={600}>
        <div className="pt-10">
          <div className="max-h-[calc(100vh-115px)] overflow-y-auto pr-3 ">
            {selectedInvoice && <InvoiceCard invoice={selectedInvoice} defaultOpened={true} />}
          </div>
        </div>
      </Modal>
    </div>
  );
});
